import { useCallback } from 'react';

import { api, setAuthFailureHandler } from '@/lib/axios';
import { firebaseSignOut, signInWithGoogle } from '@/lib/firebase';
import { setAccessToken } from '@/lib/token';
import { realtime } from '@/lib/ws';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { ApiResponse } from '@/types/api';
import type { RefreshResponse, UserRole } from '@/types/domain';

import { fetchMe, loginWithFirebase, logout as logoutApi } from './api';
import { setAuthenticated, setGuest } from './authSlice';

export function useAuth() {
  const dispatch = useAppDispatch();
  const { status, me } = useAppSelector((s) => s.auth);

  /** Full sign-in: Google popup → backend token → load profile. */
  const login = useCallback(async () => {
    const idToken = await signInWithGoogle();
    const token = await loginWithFirebase(idToken);
    setAccessToken(token.access_token);
    const profile = await fetchMe();
    dispatch(setAuthenticated(profile));
    realtime.connect();
  }, [dispatch]);

  /** Boot: mint an access token from the refresh cookie and load the profile in
   *  one single round trip (the /refresh response now embeds /me data). */
  const bootstrap = useCallback(async () => {
    try {
      const res = await api.post<ApiResponse<RefreshResponse>>('/auth/refresh', {});
      const { access_token, me } = res.data.data;
      setAccessToken(access_token);
      dispatch(setAuthenticated(me));
      realtime.connect();
    } catch {
      setAccessToken(null);
      dispatch(setGuest());
    }
  }, [dispatch]);

  const signOut = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      /* ignore — clear locally regardless */
    }
    await firebaseSignOut().catch(() => undefined);
    realtime.disconnect();
    setAccessToken(null);
    dispatch(setGuest());
  }, [dispatch]);

  const forceGuest = useCallback(() => {
    realtime.disconnect();
    setAccessToken(null);
    dispatch(setGuest());
  }, [dispatch]);

  const hasPermission = useCallback(
    (permission: string) => me?.permissions.includes(permission) ?? false,
    [me],
  );
  const hasRole = useCallback(
    (...roles: UserRole[]) => (me ? roles.includes(me.user.role) : false),
    [me],
  );

  // Wire the axios refresh-failure hook to drop the session.
  setAuthFailureHandler(forceGuest);

  return {
    status,
    me,
    isAuthenticated: status === 'authenticated',
    isLoading: status === 'loading',
    login,
    bootstrap,
    signOut,
    hasPermission,
    hasRole,
  };
}
